import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import Select from "react-select";
import styles from "./GlobalSelectMetrics.module.scss";
import metricsConfig from "../metricsConfig";
import {
  calculateAverages,
  transformDataMonthlyOrYearly,
} from "../functionUtilsStats";

export default function GlobalSelectMetrics({ dataForChart, timeInterval, setTimeInterval, selectedMetrics, setSelectedMetrics }) {
  const { t } = useTranslation();
  const [includeSaturday, setIncludeSaturday] = useState(false);
  const [transformedData, setTransformedData] = useState([]);

  const metricOptions = Object.keys(metricsConfig).map((key) => ({
    value: key,
    label: metricsConfig[key].title,
  }));

  const timeIntervalOptions = [
    { value: "daily", label: t("stats.day_by_day") },
    { value: "weekly", label: t("stats.week_by_week") },
    { value: "monthly", label: t("stats.month_by_month") },
    { value: "yearly", label: t("stats.year_by_year") },
  ];

  const handleMetricsChange = (selectedOptions) => {
    setSelectedMetrics(selectedOptions || []);
  };

  const handleTimeIntervalChange = (selectedOption) => {
    setTimeInterval(selectedOption.value);
  };

  useEffect(() => {
    const transformed = transformDataMonthlyOrYearly(
      dataForChart,
      timeInterval,
      includeSaturday
    );
    setTransformedData(transformed);
  }, [dataForChart, timeInterval, includeSaturday]);

  const renderLines = () => {
    return selectedMetrics.map(({ value }) => {
      if (metricsConfig[value]) {
        const { color, title } = metricsConfig[value];
        return (
          <Line
            key={value}
            type="monotone"
            dataKey={value}
            stroke={color}
            name={title}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        );
      }
      return null;
    });
  };

  const averages = calculateAverages(transformedData, selectedMetrics);

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarGroup}>
          <label>{t("stats.metrics_label")}</label>
          <Select
            isMulti
            options={metricOptions}
            value={selectedMetrics}
            onChange={handleMetricsChange}
            placeholder={t("stats.select_metrics_placeholder")}
          />
        </div>
        <div className={`${styles.toolbarGroup} ${styles.toolbarGroupSmall}`}>
          <label>{t("stats.interval_label")}</label>
          <Select
            options={timeIntervalOptions}
            value={timeIntervalOptions.find(
              (option) => option.value === timeInterval
            )}
            onChange={handleTimeIntervalChange}
            placeholder={t("stats.interval_placeholder")}
          />
        </div>
        {timeInterval === "daily" && (
          <div className={`${styles.toolbarGroup} ${styles.toolbarGroupSmall}`}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={includeSaturday}
                onChange={(e) => setIncludeSaturday(e.target.checked)}
              />
              {t("stats.include_saturdays")}
            </label>
          </div>
        )}
      </div>

      {/* Legend bar */}
      {selectedMetrics.length > 0 && (
        <div className={styles.legendBar}>
          {selectedMetrics.map(({ value }) => (
            <div key={value} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{ background: metricsConfig[value]?.color }}
              />
              {metricsConfig[value]?.title}
              <span className={styles.legendAvg}>
                (moy: {averages[value]?.toFixed(1)})
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      <div className={styles.chartArea}>
        <ResponsiveContainer width="100%" height={450}>
          <LineChart data={transformedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              axisLine={{ stroke: '#e0e0e0' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              axisLine={{ stroke: '#e0e0e0' }}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #e0e0e0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontSize: '0.85em',
              }}
            />
            {renderLines()}
            {selectedMetrics.map(({ value }) => (
              <ReferenceLine
                key={`avg-${value}`}
                y={averages[value]}
                stroke={metricsConfig[value]?.color || 'red'}
                strokeDasharray="6 4"
                strokeOpacity={0.5}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
